import { DateTime, Settings } from 'luxon';
import i18n from '../i18n/config';

/**
 * Sets up Luxon with the current i18n locale
 */
export const setupLuxon = () => {
  // Get the current language from i18n
  const currentLanguage = i18n.language || 'en';

  // Set Luxon's default locale based on i18n language
  Settings.defaultLocale = currentLanguage;
};

/**
 * Formats a UTC date string to the user's local timezone
 * @param dateString - UTC date string from API
 * @param format - Optional format string (defaults to datetime format)
 * @returns Formatted date string in user's timezone
 */
export const formatDateTime = (dateString: string, format?: string): string => {
  if (!dateString) return '';

  // Parse the UTC date string
  const dateTime = DateTime.fromISO(dateString, { zone: 'utc' });

  // Convert to local timezone
  const localDateTime = dateTime.toLocal();

  // Return formatted date
  if (format) {
    return localDateTime.toFormat(format);
  }

  // Default format: "Jan 1, 2023, 12:34 PM"
  return localDateTime.toLocaleString(DateTime.DATETIME_SHORT);
};

/**
 * Formats a UTC date string to the user's local timezone (date only)
 * @param dateString - UTC date string from API
 * @returns Formatted date string in user's timezone
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  // Parse the UTC date string
  const dateTime = DateTime.fromISO(dateString, { zone: 'utc' });

  // Convert to local timezone
  const localDateTime = dateTime.toLocal();

  // Return formatted date (date only)
  return localDateTime.toLocaleString(DateTime.DATE_MED);
};

/**
 * Formats a PostgreSQL range string or object to display dates
 * @param validityRange - String in format "[YYYY-MM-DD,YYYY-MM-DD)" or object with lower and upper properties
 * @returns Formatted date range string
 */
export const formatValidityRange = (
  validityRange: string | { lower: string; upper: string }
): string => {
  if (!validityRange) return '-';

  let lower: string;
  let upper: string;

  if (typeof validityRange === 'string') {
    // Remove the brackets/parentheses and split the dates
    [lower, upper] = validityRange.slice(1, -1).split(',');
  } else {
    lower = validityRange.lower;
    upper = validityRange.upper;
  }

  if (!lower || !upper) return '-';

  const lowerDate = DateTime.fromFormat(lower, 'yyyy-MM-dd');
  const upperDate = DateTime.fromFormat(upper, 'yyyy-MM-dd').minus({ days: 1 });

  if (!lowerDate.isValid || !upperDate.isValid) return '-';

  return `${lowerDate.toLocaleString(DateTime.DATE_SHORT)} - ${upperDate.toLocaleString(DateTime.DATE_SHORT)}`;
};

// Initialize Luxon with current locale
setupLuxon();

// Listen for language changes and update Luxon's locale
i18n.on('languageChanged', (lng) => {
  Settings.defaultLocale = lng;
});
